import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import type { Rol } from '@tanteo/compartido';
import type { AltaVerificada } from './alta-pendiente';
import { miPerfilPlataforma, type PerfilPlataforma } from './api-panel';
import { supabase } from './supabase';

export interface Membresia {
  membresiaId: string;
  negocioId: string;
  negocioNombre: string;
  negocioSlug: string;
  prefijo: string;
  esSintetico: boolean;
  rol: Rol;
  nombreVisible: string;
  nombres: string | null;
  apellidos: string | null;
  telefono: string | null;
  documentoTipo: string | null;
  documentoNumero: string | null;
  /** Datos del negocio, para el Perfil: el nombre y el teléfono se editan, el documento no. */
  negocioTelefono: string | null;
  negocioDocTipo: string | null;
  negocioDocNumero: string | null;
  /** 'plataforma' = la creó entrar_sandbox; no es un negocio de esta persona. */
  origen: 'negocio' | 'plataforma';
}

interface EstadoApp {
  cargando: boolean;
  sesion: Session | null;
  /** Negocios donde la persona trabaja. No incluye entornos de prueba. */
  negociosPropios: Membresia[];
  negocio: Membresia | null;
  /**
   * Perfil de quien opera el panel: rol, permisos e identidad; vive aquí porque el encabezado del
   * layout necesita saber qué secciones dibujar. `null` = todavía sin resolver.
   */
  perfilPanel: PerfilPlataforma | null;
  esSuperadmin: boolean | null;
  /**
   * La consulta de membresías falló. Es distinto de «no tienes negocios»: sin distinguirlos, un
   * parpadeo de red deja la lista vacía y la aplicación ofrece crear un negocio a quien ya tiene uno.
   */
  fallóCarga: boolean;
  /**
   * La lectura del perfil de plataforma falló. Es distinto de «no operas el panel»: sin
   * distinguirlos, quien opera el panel queda tratado como quien no lo opera.
   */
  fallóPerfilPanel: boolean;
  /**
   * El alta capturada en el registro, con su autorización, hasta que `/crear-negocio` crea el
   * negocio. Vive aquí porque el proveedor sobrevive al cambio de pantalla que trae la sesión nueva.
   */
  altaVerificada: AltaVerificada | null;
  dejarAltaVerificada: (alta: AltaVerificada | null) => void;
  /** null limpia el negocio activo (salir de un sandbox, volver al selector). */
  elegirNegocio: (negocioId: string | null) => void;
  recargarMembresias: () => Promise<void>;
  reintentarPerfilPanel: () => Promise<void>;
  salir: () => Promise<void>;
}

const Contexto = createContext<EstadoApp | null>(null);
const CLAVE_NEGOCIO = 'tanteo-negocio';

export function ProveedorApp({ children }: { children: ReactNode }) {
  const [cargando, setCargando] = useState(true);
  const [sesion, setSesion] = useState<Session | null>(null);
  const [membresias, setMembresias] = useState<Membresia[]>([]);
  // null = todavía sin resolver; los guards no deben redirigir mientras esté pendiente.
  const [perfilPanel, setPerfilPanel] = useState<PerfilPlataforma | null>(null);
  const [fallóCarga, setFallóCarga] = useState(false);
  const [fallóPerfilPanel, setFallóPerfilPanel] = useState(false);
  const [altaVerificada, setAltaVerificada] = useState<AltaVerificada | null>(null);
  const [negocioId, setNegocioId] = useState<string | null>(() => localStorage.getItem(CLAVE_NEGOCIO));

  // El filtro por usuario es obligatorio: al propietario la política de RLS le deja ver todas las
  // membresías de su negocio, así que sin él podría resolverse con la fila de otra persona.
  const cargarMembresias = useCallback(async (usuarioId: string) => {
    const { data, error } = await supabase
      .from('membresias')
      .select(
        'id, rol, nombre_visible, nombres, apellidos, telefono, documento_tipo, documento_numero, origen, negocios ( id, nombre, slug, comprobante_prefijo, es_sintetico, telefono, documento_tipo, documento_numero )'
      )
      .eq('usuario_id', usuarioId)
      .is('eliminado_en', null);
    if (error) throw error;
    const lista: Membresia[] = (data ?? [])
      .filter((m) => m.negocios)
      .map((m) => {
        const n = m.negocios as unknown as {
          id: string;
          nombre: string;
          slug: string;
          comprobante_prefijo: string;
          es_sintetico: boolean;
          telefono: string | null;
          documento_tipo: string | null;
          documento_numero: string | null;
        };
        return {
          membresiaId: m.id as string,
          negocioId: n.id,
          negocioNombre: n.nombre,
          negocioSlug: n.slug,
          prefijo: n.comprobante_prefijo,
          esSintetico: n.es_sintetico,
          rol: m.rol as Rol,
          nombreVisible: m.nombre_visible as string,
          nombres: (m.nombres as string) ?? null,
          apellidos: (m.apellidos as string) ?? null,
          telefono: (m.telefono as string) ?? null,
          documentoTipo: (m.documento_tipo as string) ?? null,
          documentoNumero: (m.documento_numero as string) ?? null,
          negocioTelefono: n.telefono ?? null,
          negocioDocTipo: n.documento_tipo ?? null,
          negocioDocNumero: n.documento_numero ?? null,
          origen: (m.origen as Membresia['origen']) ?? 'negocio',
        };
      });
    setMembresias(lista);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSesion(data.session);
      if (!data.session) setCargando(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_evento, s) => setSesion(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  /**
   * Un solo viaje: el rol de plataforma, sus permisos y su identidad llegan juntos. Con un reintento
   * antes de darla por perdida, porque la primera lectura corre junto a la sesión recién abierta.
   */
  const cargarPerfilPanel = useCallback(async () => {
    for (const intento of [1, 2]) {
      try {
        setPerfilPanel(await miPerfilPlataforma());
        setFallóPerfilPanel(false);
        return;
      } catch {
        if (intento === 2) {
          setPerfilPanel(null);
          setFallóPerfilPanel(true);
        }
      }
    }
  }, []);

  // Depende de quién entró, no del objeto de sesión: Auth entrega uno nuevo al volver a la pestaña y al
  // refrescar el token, y recargar por eso desmontaría la aplicación con un cuadre a medio contar.
  const usuarioId = sesion?.user.id ?? null;
  useEffect(() => {
    if (!usuarioId) {
      setMembresias([]);
      setPerfilPanel(null);
      setFallóPerfilPanel(false);
      setAltaVerificada(null);
      return;
    }
    setCargando(true);
    cargarMembresias(usuarioId)
      .then(() => setFallóCarga(false))
      .catch(() => setFallóCarga(true)) // la lista se deja como estaba: vaciarla mentiría
      .finally(() => setCargando(false));
    void cargarPerfilPanel();
  }, [usuarioId, cargarMembresias, cargarPerfilPanel]);

  const elegirNegocio = useCallback((id: string | null) => {
    if (id) localStorage.setItem(CLAVE_NEGOCIO, id);
    else localStorage.removeItem(CLAVE_NEGOCIO);
    setNegocioId(id);
  }, []);

  const salir = useCallback(async () => {
    await supabase.auth.signOut();
    localStorage.removeItem(CLAVE_NEGOCIO);
    setNegocioId(null);
  }, []);

  const negociosPropios = useMemo(() => membresias.filter((m) => m.origen === 'negocio'), [membresias]);

  // La resolución automática solo mira los negocios propios: si mirara todos, quien tenga
  // una sola membresía de sandbox quedaría encerrado dentro del entorno de prueba.
  const negocio = useMemo(() => {
    const elegido = negocioId ? membresias.find((m) => m.negocioId === negocioId) : undefined;
    return elegido ?? (negociosPropios.length === 1 ? negociosPropios[0]! : null);
  }, [membresias, negociosPropios, negocioId]);

  const recargarMembresias = useCallback(async () => {
    if (!sesion) return;
    await cargarMembresias(sesion.user.id);
    setFallóCarga(false);
  }, [sesion, cargarMembresias]);

  const dejarAltaVerificada = useCallback((alta: AltaVerificada | null) => setAltaVerificada(alta), []);

  // Se deriva del perfil para que no haya dos fuentes de la misma verdad.
  const esSuperadmin = perfilPanel === null ? null : perfilPanel.rol !== null;

  const valor = useMemo(
    () => ({
      cargando,
      sesion,
      negociosPropios,
      negocio,
      perfilPanel,
      esSuperadmin,
      fallóCarga,
      fallóPerfilPanel,
      altaVerificada,
      dejarAltaVerificada,
      elegirNegocio,
      recargarMembresias,
      reintentarPerfilPanel: cargarPerfilPanel,
      salir,
    }),
    [
      cargando,
      sesion,
      negociosPropios,
      negocio,
      perfilPanel,
      esSuperadmin,
      fallóCarga,
      fallóPerfilPanel,
      altaVerificada,
      dejarAltaVerificada,
      elegirNegocio,
      recargarMembresias,
      cargarPerfilPanel,
      salir,
    ]
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useApp(): EstadoApp {
  const ctx = useContext(Contexto);
  if (!ctx) throw new Error('useApp requiere ProveedorApp');
  return ctx;
}
